import React, { useState, useEffect } from "react";
import { db } from "../../firebase";
import { collection, getDocs } from "firebase/firestore";
import "./calendar.css";

export default function Calendar() {
  const WEEKS_FROM_TODAY = 4; // Show availability for the next X weeks
  const today = new Date();

  // Adjust starting month if today is the last or second last day of the month
  const adjustedToday = new Date(today);
  const lastDayOfMonth = new Date(
    today.getFullYear(),
    today.getMonth() + 1,
    0
  ).getDate();
  if (today.getDate() >= lastDayOfMonth - 1) {
    adjustedToday.setMonth(adjustedToday.getMonth() + 1);
  }

  // Calculate the maximum date to show
  const maxDate = new Date(today);
  maxDate.setDate(maxDate.getDate() + 7 * WEEKS_FROM_TODAY);

  const [currentMonth, setCurrentMonth] = useState(adjustedToday.getMonth());
  const [currentYear, setCurrentYear] = useState(adjustedToday.getFullYear());
  const [unavailableDates, setUnavailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableTimeRanges, setAvailableTimeRanges] = useState([]);

  // Minimum month/year to prevent navigating to past months
  const minMonth = adjustedToday.getMonth();
  const minYear = adjustedToday.getFullYear();

  // Calendar calculations
  const firstDay = new Date(currentYear, currentMonth, 1).getDay();
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  // Helper to generate hour ranges excluding break hours
  function generateHourRange(start, end, breakHours = []) {
    let hours = Array.from({ length: end - start }, (_, i) => i + start);
    return hours.filter((hour) => !breakHours.includes(hour));
  }

  // Default availability hours (24-hour format)
  const DEFAULT_AVAILABILITY = {
    0: generateHourRange(10, 21, [13, 18]), // Sunday
    1: generateHourRange(20, 21), // Monday
    2: generateHourRange(20, 21), // Tuesday
    3: generateHourRange(20, 21), // Wednesday
    4: generateHourRange(20, 21), // Thursday
    5: generateHourRange(20, 21), // Friday
    6: generateHourRange(10, 21, [13, 18]), // Saturday
  };

  // Convert hours to time blocks in minutes
  function getDefaultTimeBlocksForDay(weekday) {
    const hours = DEFAULT_AVAILABILITY[weekday] || [];
    if (hours.length === 0) return [];

    hours.sort((a, b) => a - b);
    const ranges = [];

    let start = hours[0];
    let prev = hours[0];

    for (let i = 1; i < hours.length; i++) {
      if (hours[i] !== prev + 1) {
        ranges.push([start * 60, (prev + 1) * 60]);
        start = hours[i];
      }
      prev = hours[i];
    }
    ranges.push([start * 60, (prev + 1) * 60]);
    return ranges;
  }

  // Fetch unavailable dates from Firestore on component mount
  useEffect(() => {
    const fetchUnavailableDates = async () => {
      const snapshot = await getDocs(collection(db, "calendarUnavailability"));
      const data = snapshot.docs.map((doc) => doc.data());
      setUnavailableDates(data);
    };
    fetchUnavailableDates();
  }, []);

  // Check if a day is in the past
  const isPastDay = (day) => {
    if (day === null) return false;
    const date = new Date(currentYear, currentMonth, day);
    return (
      date < new Date(today.getFullYear(), today.getMonth(), today.getDate())
    );
  };

  // Check if a day is beyond the max date
  const isBeyondMaxDate = (day) => {
    if (day === null) return false;
    const date = new Date(currentYear, currentMonth, day);
    return date > maxDate;
  };

  // Check if a day is unavailable
  const isUnavailableDay = (day) => {
    if (day === null || isBeyondMaxDate(day)) return true;

    const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(
      2,
      "0"
    )}-${String(day).padStart(2, "0")}`;
    const todayStr = `${today.getFullYear()}-${String(
      today.getMonth() + 1
    ).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    const tomorrowStr = `${tomorrow.getFullYear()}-${String(
      tomorrow.getMonth() + 1
    ).padStart(2, "0")}-${String(tomorrow.getDate()).padStart(2, "0")}`;

    if (dateStr === todayStr || dateStr === tomorrowStr) return true;

    return unavailableDates.some(
      (entry) =>
        entry.date === dateStr &&
        (entry.startTime === "00:00:00" || entry.startTime === "0:00:00") &&
        entry.endTime === "23:59:59"
    );
  };

  // Navigation button states
  const isPrevDisabled = () => {
    return (
      currentYear < minYear ||
      (currentYear === minYear && currentMonth <= minMonth)
    );
  };

  const isNextDisabled = () => {
    const nextMonthDate = new Date(currentYear, currentMonth + 1, 1);
    return nextMonthDate > maxDate;
  };
  //

  // Handlers for month navigation
  const prevMonth = () => {
    if (isPrevDisabled()) return;
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
    setSelectedDate(null);
    setAvailableTimeRanges([]);
  };

  const nextMonth = () => {
    if (isNextDisabled()) return;
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
    setSelectedDate(null);
    setAvailableTimeRanges([]);
  };
  //

  // Generate dates for the current month view
  const dates = [];
  for (let i = 0; i < firstDay; i++) dates.push(null);
  for (let d = 1; d <= daysInMonth; d++) dates.push(d);

  const handleDateClick = (day) => {
    if (isPastDay(day) || isUnavailableDay(day) || day === null) return;

    const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(
      2,
      "0"
    )}-${String(day).padStart(2, "0")}`;
    setSelectedDate(dateStr);

    const weekday = new Date(currentYear, currentMonth, day).getDay();
    const defaultAvailable = getDefaultTimeBlocksForDay(weekday);

    const unavailableRanges = unavailableDates
      .filter((entry) => entry.date === dateStr)
      .map((entry) => {
        if (!entry.startTime || !entry.endTime) return null;
        const [sh, sm] = entry.startTime.split(":").map(Number);
        const [eh, em] = entry.endTime.split(":").map(Number);
        let start = sh * 60 + sm - 30;
        let end = eh * 60 + em + 30;
        return [Math.max(0, start), Math.min(1440, end)];
      })
      .filter(Boolean);

    const available = subtractTimeRanges(defaultAvailable, unavailableRanges);
    setAvailableTimeRanges(available);
  };

  // Handle date click to calculate available time ranges and notify parent
  // const handleDateClick = (day) => {
  //   if (isPastDay(day) || isUnavailableDay(day) || day === null) return;

  //   const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(
  //     2,
  //     "0"
  //   )}-${String(day).padStart(2, "0")}`;

  //   const weekday = new Date(currentYear, currentMonth, day).getDay();
  //   const defaultAvailable = getDefaultTimeBlocksForDay(weekday);

  //   const unavailableRanges = unavailableDates
  //     .filter((entry) => entry.date === dateStr)
  //     .map((entry) => {
  //       if (!entry.startTime || !entry.endTime) return null;
  //       const [sh, sm] = entry.startTime.split(":").map(Number);
  //       const [eh, em] = entry.endTime.split(":").map(Number);
  //       let start = sh * 60 + sm - 30;
  //       let end = eh * 60 + em + 30;
  //       return [Math.max(0, start), Math.min(1440, end)];
  //     })
  //     .filter(Boolean);

  //   const available = subtractTimeRanges(defaultAvailable, unavailableRanges);

  //   // Notify parent
  //   onSelectDate?.(dateStr);
  //   onAvailabilityChange?.(available);
  // };

  // Subtract unavailable time ranges from available ones
  function subtractTimeRanges(available, unavailables) {
    let result = [...available];
    for (const [uStart, uEnd] of unavailables) {
      const temp = [];
      for (const [aStart, aEnd] of result) {
        if (uEnd <= aStart || uStart >= aEnd) {
          temp.push([aStart, aEnd]);
        } else {
          if (uStart > aStart) temp.push([aStart, uStart]);
          if (uEnd < aEnd) temp.push([uEnd, aEnd]);
        }
      }
      result = temp;
    }
    return result.filter(([start, end]) => end - start >= 15);
  }

  // Format minutes to hh:mm am/pm
  function formatTime(minutes) {
    const hour = Math.floor(minutes / 60);
    const min = minutes % 60;
    const ampm = hour >= 12 ? "pm" : "am";
    const hour12 = hour % 12 === 0 ? 12 : hour % 12;
    return `${hour12}:${min.toString().padStart(2, "0")}${ampm}`;
  }

  // Render calendar rows
  function renderCalendarRows() {
    const weeks = Math.ceil(dates.length / 7);
    const rows = [];

    for (let weekIdx = 0; weekIdx < weeks; weekIdx++) {
      const cells = dates
        .slice(weekIdx * 7, weekIdx * 7 + 7)
        .map((date, idx) => {
          const isUnavailable = isUnavailableDay(date);
          const isPast = isPastDay(date);
          const isBeyond = isBeyondMaxDate(date);
          const dateStr =
            date &&
            `${currentYear}-${String(currentMonth + 1).padStart(
              2,
              "0"
            )}-${String(date).padStart(2, "0")}`;

          const isSelected = selectedDate && selectedDate === dateStr;

          const classNames = [
            "calendar-cell",
            isUnavailable || isPast || isBeyond ? "unavailable" : "available",
            isSelected ? "selected" : "",
          ].join(" ");

          return (
            <td
              key={idx}
              className={classNames}
              onClick={() => handleDateClick(date)}
            >
              {date || ""}
            </td>
          );
        });

      rows.push(<tr key={weekIdx}>{cells}</tr>);
    }

    return rows;
  }

  /**
   *
   * @param {*} monthFormat format of month: "long" or "short"
   * @returns
   */
  function currentMonthStr(monthFormat = "long") {
    if (monthFormat !== "long" && monthFormat !== "short") {
      monthFormat = "long";
    }
    return `${new Date(currentYear, currentMonth).toLocaleString("default", {
      month: monthFormat,
    })}`;
  }

  return (
    <div className="calendar-container">
      <div className="calendar-header">
        <button
          onClick={prevMonth}
          disabled={isPrevDisabled()}
          className={`${isPrevDisabled() ? "disabled" : ""}`}
        >
          &lt;
        </button>
        <span>{`${currentMonthStr()} ${currentYear}`}</span>
        <button
          onClick={nextMonth}
          disabled={isNextDisabled()}
          className={`${isNextDisabled() ? "disabled" : ""}`}
        >
          &gt;
        </button>
      </div>

      <table className="calendar-table">
        <thead>
          <tr>
            {daysOfWeek.map((day) => (
              <th key={day} className="calendar-day-header">
                {day}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{renderCalendarRows()}</tbody>
      </table>

      {selectedDate && (
        <div className="calendar-availability fade-in">
          <strong>
            Available times for{" "}
            {`${currentMonthStr("short")} ${selectedDate.slice(-2)}`}:
          </strong>
          {availableTimeRanges.length === 0 ? (
            <div className="no-availability">No available time ranges.</div>
          ) : (
            <ul>
              {availableTimeRanges.map(([start, end], idx) => (
                <li key={idx}>
                  {formatTime(start)} - {formatTime(end)}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
